import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';

const PREFIX = 'MetricsContent';

const classes = {
  rangeColor: `${PREFIX}-rangeColor`,
  targetValueMissing: `${PREFIX}-targetValueMissing`,
  secondaryText: `${PREFIX}-secondaryText`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.rangeColor}`]: {
    height: '16px',
    width: '16px',
    borderRadius: '50%',
    marginTop: 0,
    marginRight: theme.spacing(1),
    display: 'inline-block'
  },

  [`& .${classes.targetValueMissing}`]: {
    height: '18px'
  },

  [`& .${classes.secondaryText}`]: {
    paddingLeft: theme.spacing(3)
  }
}));

export interface Measure {
  name?: string;
  value?: string;
  rangecolor?: string;
  periodname?: string;
  children?: Measure[];
  mainmeasure?: boolean;
  targetvalue?: string;
}

interface MeasureProps {
  measure?: Measure;
}

const MetricsContent = ({ measure = {} as Measure }: MeasureProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const iconOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  const measureData = (measure_: Measure, main: boolean = false) => {
    const showValue = () => {
      return measure_.value ? `: ${measure_.value} (${measure_.periodname})` : '';
    };

    return (
      <ListItemText
        style={{ marginBottom: '0px', marginTop: '0px' }}
        primary={
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span
              data-testid="rangecolor"
              style={{
                backgroundColor:
                  measure_.rangecolor && measure_.rangecolor !== '#000000'
                    ? measure_.rangecolor
                    : 'lightgray'
              }}
              className={classes.rangeColor}
            />
            {measure_.name}
            {showValue()}
            {main && (
              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                <IconButton size="small" aria-label="expand-metrics" onClick={iconOnClick}>
                  <ExpandMore transform={isExpanded ? 'rotate(180)' : 'rotate(0)'} />
                </IconButton>
              </Tooltip>
            )}
          </span>
        }
        secondary={
          measure_.targetvalue ? (
            `Target: ${measure_.targetvalue}`
          ) : (
            <span className={classes.targetValueMissing} />
          )
        }
        classes={{ secondary: classes.secondaryText }}
        primaryTypographyProps={{
          variant: main ? 'subtitle1' : 'body2',
          color: main ? 'primary' : 'textPrimary'
        }}
        secondaryTypographyProps={{ variant: 'caption', color: 'textSecondary' }}
      />
    );
  };

  return measure && measure.name !== undefined ? (
    <Root>
      <List disablePadding data-testid="metrics-list">
        <ListItem data-testid="main-metrics" dense>
          {measureData(measure, true)}
        </ListItem>
        <Collapse
          data-testid="expanded-group"
          in={isExpanded}
          timeout={{ enter: 200, exit: 250 }}
          unmountOnExit
        >
          {measure.children && measure.children.length > 0
            ? measure.children.map((measure_: Measure) => {
                return (
                  <ListItem dense key={measure_.name} data-testid="sub-metrics">
                    {measureData(measure_)}
                  </ListItem>
                );
              })
            : null}
        </Collapse>
      </List>
    </Root>
  ) : null;
};

export default MetricsContent;
